/**
 * useWebSocketManager - Advanced WebSocket connection manager
 * Handles multiple WebSocket connections with automatic reconnection and message routing.
 *
 * Private channels use SOC2 short-lived tickets (mint on every connect/reconnect).
 * Never place long-lived tokens or apiKey in the WebSocket URL.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useErrorContext } from '@/shared/components/error/ErrorContext';
import {
  connectSecureWs,
  isPrivateChannel,
  type WsChannel,
  WsTicketError,
} from '@/infrastructure/websocket/secureSocket';

// WebSocket connection types
interface WebSocketConfig {
  /** Static URL (legacy). Prefer `channel` for ticket-based auth. */
  url?: string;
  /** Channel for ticket flow: `orders`, `notifications`, `market-data`, … */
  channel?: WsChannel;
  /** Mint ticket on connect/reconnect (default true when channel set) */
  useTicket?: boolean;
  /** Require ticket (default: private channels only) */
  requireTicket?: boolean;
  softFailAuth?: boolean;
  protocols?: string[];
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  heartbeatInterval?: number;
  timeout?: number;
  onOpen?: (event: Event) => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event) => void;
  onMessage?: (data: any) => void;
}

interface ConnectionState {
  status: 'connecting' | 'connected' | 'disconnected' | 'error' | 'reconnecting';
  lastConnected?: Date;
  reconnectAttempts: number;
  latency?: number;
  messageCount: number;
  errorCount: number;
}

interface WebSocketConnection {
  id: string;
  config: WebSocketConfig;
  socket?: WebSocket;
  state: ConnectionState;
  subscriptions: Set<string>;
}

interface UseWebSocketManagerReturn {
  connections: Record<string, WebSocketConnection>;
  connect: (id: string, config: WebSocketConfig) => void;
  disconnect: (id: string) => void;
  disconnectAll: () => void;
  sendMessage: (id: string, message: any) => boolean;
  subscribe: (id: string, channel: string) => void;
  unsubscribe: (id: string, channel: string) => void;
  getConnectionStatus: (id: string) => ConnectionState['status'];
  isConnected: (id: string) => boolean;
  getLatency: (id: string) => number | undefined;
  reconnect: (id: string) => void;
  reconnectAll: () => void;
}

export const useWebSocketManager = (): UseWebSocketManagerReturn => {
  const [connections, setConnections] = useState<Record<string, WebSocketConnection>>({});
  const connectionsRef = useRef<Record<string, WebSocketConnection>>({});
  const heartbeatIntervals = useRef<Record<string, ReturnType<typeof setInterval>>>({});
  const reconnectTimeouts = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const connectAbort = useRef<Record<string, AbortController>>({});
  const intentionalClose = useRef<Record<string, boolean>>({});
  const { reportError } = useErrorContext();

  // Update ref when connections change
  useEffect(() => {
    connectionsRef.current = connections;
  }, [connections]);

  const updateConnectionState = useCallback((id: string, updates: Partial<ConnectionState>) => {
    setConnections((prev) => {
      const existing = prev[id];
      if (!existing) return prev;

      return {
        ...prev,
        [id]: {
          ...existing,
          state: {
            ...existing.state,
            ...updates,
          },
        },
      };
    });
  }, []);

  const createConnection = useCallback((id: string, config: WebSocketConfig): WebSocketConnection => {
    return {
      id,
      config,
      state: {
        status: 'disconnected',
        reconnectAttempts: 0,
        messageCount: 0,
        errorCount: 0,
      },
      subscriptions: new Set(),
    };
  }, []);

  const startHeartbeat = useCallback(
    (id: string) => {
      const connection = connectionsRef.current[id];
      if (!connection?.socket || connection.state.status !== 'connected') return;

      const interval = connection.config.heartbeatInterval || 30000;

      if (heartbeatIntervals.current[id]) {
        clearInterval(heartbeatIntervals.current[id]);
      }

      heartbeatIntervals.current[id] = setInterval(() => {
        const conn = connectionsRef.current[id];
        if (conn?.socket?.readyState === WebSocket.OPEN) {
          const startTime = Date.now();

          conn.socket.send(JSON.stringify({ type: 'ping', timestamp: startTime }));

          const originalOnMessage = conn.socket.onmessage;
          conn.socket.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data as string);
              if (data.type === 'pong') {
                const latency = Date.now() - (data.timestamp ?? startTime);
                updateConnectionState(id, { latency });
              }
            } catch {
              // Not a JSON message, ignore for ping/pong
            }
            if (conn.socket) {
              originalOnMessage?.call(conn.socket, event);
            }
          };
        }
      }, interval);
    },
    [updateConnectionState]
  );

  const stopHeartbeat = useCallback((id: string) => {
    if (heartbeatIntervals.current[id]) {
      clearInterval(heartbeatIntervals.current[id]);
      delete heartbeatIntervals.current[id];
    }
  }, []);

  const connectToWebSocketRef = useRef<(id: string) => void>(() => {});

  const scheduleReconnect = useCallback(
    (id: string) => {
      const connection = connectionsRef.current[id];
      if (!connection) return;

      const { reconnectInterval = 5000, maxReconnectAttempts = 5 } = connection.config;

      if (connection.state.reconnectAttempts >= maxReconnectAttempts) {
        updateConnectionState(id, { status: 'error' });
        reportError(new Error(`Max reconnection attempts reached for WebSocket ${id}`));
        return;
      }

      updateConnectionState(id, {
        status: 'reconnecting',
        reconnectAttempts: connection.state.reconnectAttempts + 1,
      });

      reconnectTimeouts.current[id] = setTimeout(() => {
        // Remint ticket on every reconnect attempt
        connectToWebSocketRef.current(id);
      }, reconnectInterval);
    },
    [updateConnectionState, reportError]
  );

  const connectToWebSocket = useCallback(
    (id: string) => {
      const connection = connectionsRef.current[id];
      if (!connection) return;

      const {
        url,
        channel,
        protocols,
        timeout = 10000,
        useTicket = true,
        requireTicket,
        softFailAuth,
      } = connection.config;

      const shouldUseTicket = useTicket !== false && Boolean(channel);
      const ticketRequired =
        requireTicket ?? (channel ? isPrivateChannel(channel) : false);
      const softAuth =
        softFailAuth ?? (channel ? !isPrivateChannel(channel) : true);

      intentionalClose.current[id] = false;
      connectAbort.current[id]?.abort();
      const abort = new AbortController();
      connectAbort.current[id] = abort;

      // Close prior socket if any
      if (connection.socket) {
        try {
          connection.socket.close(1000, 'Reconnecting');
        } catch {
          // ignore
        }
      }

      updateConnectionState(id, { status: 'connecting' });

      const attach = (socket: WebSocket) => {
        if (abort.signal.aborted || !connectionsRef.current[id]) {
          socket.close(1000, 'Connect aborted');
          return;
        }

        const connectionTimeout = setTimeout(() => {
          if (socket.readyState === WebSocket.CONNECTING) {
            socket.close();
            updateConnectionState(id, { status: 'error' });
            reportError(new Error(`WebSocket connection timeout for ${id}`));
          }
        }, timeout);

        socket.onopen = (event) => {
          clearTimeout(connectionTimeout);
          updateConnectionState(id, {
            status: 'connected',
            lastConnected: new Date(),
            reconnectAttempts: 0,
          });
          startHeartbeat(id);
          connection.config.onOpen?.(event);
        };

        socket.onclose = (event) => {
          clearTimeout(connectionTimeout);
          stopHeartbeat(id);

          if (!intentionalClose.current[id] && event.code !== 1000) {
            scheduleReconnect(id);
          } else {
            updateConnectionState(id, { status: 'disconnected' });
          }

          connection.config.onClose?.(event);
        };

        socket.onerror = (event) => {
          clearTimeout(connectionTimeout);
          updateConnectionState(id, {
            status: 'error',
            errorCount: (connectionsRef.current[id]?.state.errorCount ?? 0) + 1,
          });
          reportError(new Error(`WebSocket error for ${id}`));
          connection.config.onError?.(event);
        };

        socket.onmessage = (event) => {
          const current = connectionsRef.current[id];
          updateConnectionState(id, {
            messageCount: (current?.state.messageCount ?? 0) + 1,
          });

          try {
            const data = JSON.parse(event.data as string);
            connection.config.onMessage?.(data);
          } catch {
            connection.config.onMessage?.(event.data);
          }
        };

        setConnections((prev) => {
          const existing = prev[id];
          if (!existing) return prev;
          return {
            ...prev,
            [id]: {
              ...existing,
              socket,
            },
          };
        });
      };

      const run = async () => {
        try {
          let socket: WebSocket;

          if (shouldUseTicket && channel) {
            socket = await connectSecureWs({
              channel,
              protocols,
              requireTicket: ticketRequired,
              softFailAuth: softAuth,
              signal: abort.signal,
            });
          } else if (url) {
            socket = protocols
              ? new WebSocket(url, protocols)
              : new WebSocket(url);
          } else {
            throw new Error(
              `WebSocket ${id}: config requires channel (ticket) or url`
            );
          }

          attach(socket);
        } catch (error) {
          if (abort.signal.aborted) return;

          const isAuth =
            error instanceof WsTicketError && error.status === 401;

          updateConnectionState(id, {
            status: 'error',
            errorCount: (connectionsRef.current[id]?.state.errorCount ?? 0) + 1,
          });

          if (!isAuth) {
            reportError(error as Error);
            scheduleReconnect(id);
          }
        }
      };

      void run();
    },
    [updateConnectionState, reportError, startHeartbeat, stopHeartbeat, scheduleReconnect]
  );

  connectToWebSocketRef.current = connectToWebSocket;

  const disconnect = useCallback(
    (id: string) => {
      intentionalClose.current[id] = true;
      connectAbort.current[id]?.abort();
      delete connectAbort.current[id];

      const connection = connectionsRef.current[id];
      if (!connection) return;

      if (reconnectTimeouts.current[id]) {
        clearTimeout(reconnectTimeouts.current[id]);
        delete reconnectTimeouts.current[id];
      }
      stopHeartbeat(id);

      if (connection.socket) {
        connection.socket.close(1000, 'Manual disconnect');
      }

      setConnections((prev) => {
        const { [id]: _removed, ...rest } = prev;
        return rest;
      });
    },
    [stopHeartbeat]
  );

  const connect = useCallback(
    (id: string, config: WebSocketConfig) => {
      if (connectionsRef.current[id]) {
        disconnect(id);
      }

      const connection = createConnection(id, config);
      setConnections((prev) => ({
        ...prev,
        [id]: connection,
      }));

      // Ensure state is visible in ref before connect
      connectionsRef.current = {
        ...connectionsRef.current,
        [id]: connection,
      };

      setTimeout(() => connectToWebSocket(id), 100);
    },
    [createConnection, connectToWebSocket, disconnect]
  );

  const disconnectAll = useCallback(() => {
    Object.keys(connectionsRef.current).forEach(disconnect);
  }, [disconnect]);

  const sendMessage = useCallback(
    (id: string, message: any): boolean => {
      const connection = connectionsRef.current[id];
      if (!connection?.socket || connection.socket.readyState !== WebSocket.OPEN) {
        return false;
      }

      try {
        const messageString =
          typeof message === 'string' ? message : JSON.stringify(message);
        connection.socket.send(messageString);
        return true;
      } catch (error) {
        reportError(error as Error);
        return false;
      }
    },
    [reportError]
  );

  const subscribe = useCallback(
    (id: string, channel: string) => {
      const connection = connectionsRef.current[id];
      if (!connection) return;

      connection.subscriptions.add(channel);

      if (connection.socket?.readyState === WebSocket.OPEN) {
        sendMessage(id, {
          type: 'subscribe',
          channel,
        });
      }
    },
    [sendMessage]
  );

  const unsubscribe = useCallback(
    (id: string, channel: string) => {
      const connection = connectionsRef.current[id];
      if (!connection) return;

      connection.subscriptions.delete(channel);

      if (connection.socket?.readyState === WebSocket.OPEN) {
        sendMessage(id, {
          type: 'unsubscribe',
          channel,
        });
      }
    },
    [sendMessage]
  );

  const getConnectionStatus = useCallback((id: string): ConnectionState['status'] => {
    return connectionsRef.current[id]?.state.status || 'disconnected';
  }, []);

  const isConnected = useCallback(
    (id: string): boolean => {
      return getConnectionStatus(id) === 'connected';
    },
    [getConnectionStatus]
  );

  const getLatency = useCallback((id: string): number | undefined => {
    return connectionsRef.current[id]?.state.latency;
  }, []);

  const reconnect = useCallback(
    (id: string) => {
      const connection = connectionsRef.current[id];
      if (!connection) return;

      updateConnectionState(id, { reconnectAttempts: 0 });

      // Keep config; remint ticket on next connectToWebSocket
      const cfg = connection.config;
      disconnect(id);
      setTimeout(() => connect(id, cfg), 1000);
    },
    [connect, disconnect, updateConnectionState]
  );

  const reconnectAll = useCallback(() => {
    Object.keys(connectionsRef.current).forEach(reconnect);
  }, [reconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnectAll();
      Object.values(heartbeatIntervals.current).forEach(clearInterval);
      Object.values(reconnectTimeouts.current).forEach(clearTimeout);
      Object.values(connectAbort.current).forEach((a) => a.abort());
    };
  }, [disconnectAll]);

  return {
    connections,
    connect,
    disconnect,
    disconnectAll,
    sendMessage,
    subscribe,
    unsubscribe,
    getConnectionStatus,
    isConnected,
    getLatency,
    reconnect,
    reconnectAll,
  };
};

export default useWebSocketManager;
